import { IBodyGoldPrice, ITemplateChart } from "api/types/_campaign";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import CopyWrapper from "components/Common/CopyWrapper";
import DropdownOption from "components/Common/DropdownOption";
import SearchTemplateChart from "components/Common/SearchTemplateChart";
import { useFormik } from "formik";
import { CATEGORY_TEMPLATE_VOTE_KEYWORD_OPTIONS, TEMPLATE_ELEMENT_TYPE_BOOSTING_CONTENT } from "helpers/constans";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import 'react-json-pretty/themes/monikai.css';
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { postBodyVote, putBodyVote } from "store/campaign/thunk";
import * as Yup from "yup";

export interface Props {
    isOpen?: boolean;
    detail?: IBodyGoldPrice | null,
    templateChart?: ITemplateChart[],
    variables: any[],
    onClose?: () => void
    triggerRefresh?: (type?: string) => void;
}

const BodyForm = ({
    isOpen = false,
    detail = null,
    templateChart = [],
    variables = [],
    onClose,
    triggerRefresh
}: Props) => {
    const { t } = useTranslation();

    const TEMPLATE_CHART_OPTIONS_LANG: any = templateChart?.map((tmpl: any, index: number) => ({ id: String(tmpl?.id), name: tmpl?.name, name_korea: tmpl?.name_korea, code: tmpl?.code, image_url: tmpl?.image_url }));;

    const CATEGORY_TEMPLATE_VOTE_KEYWORD_OPTIONS_LANG = CATEGORY_TEMPLATE_VOTE_KEYWORD_OPTIONS?.map((item: any) => ({ value: `${item?.value}`, label: t(item?.label) }));

    const [search, setSearch] = useState<string>('');

    const [isFullView, setIsFullView] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onCloseClick = () => {
        onClose && onClose();
    }
    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            const data = {
                name: values?.name || '',
                content: values?.content || '',
                chart_title: values?.chart_title || '',
                chart_source: values?.chart_source || '',
                table_title: values?.table_title || '',
                chart_code: values?.chart_code?.value || TEMPLATE_CHART_OPTIONS_LANG[0]?.value,
                category: Number(values?.category?.value),
                ...(!!detail?.id ? {} : {
                    element_type: TEMPLATE_ELEMENT_TYPE_BOOSTING_CONTENT.BODY,
                })
            };
            const response: any = !!detail?.id ? await putBodyVote(detail?.id, data) : await postBodyVote(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh();
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(`${t("This is required")}`),
        content: Yup.string().required(`${t("This is required")}`),
        chart_title: Yup.string().nullable(),
        chart_source: Yup.string().nullable(),
        table_title: Yup.string().nullable(),
        chart_code: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string().required(`${t("This is required")}`),
        }).required(`${t("This is required")}`),
        category: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string().required(`${t("This is required")}`),
        }).required(`${t("This is required")}`),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            content: '',
            chart_title: '',
            chart_source: '',
            table_title: '',
            chart_code: null,
            category: null,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                const _name = detail?.name || '';
                const _content = detail?.content || '';
                const _chart_title = detail?.chart_title || '';
                const _chart_source = detail?.chart_source || '';
                const _table_title = detail?.table_title || '';
                const _chart_code = !!detail?.chart_code ? { value: TEMPLATE_CHART_OPTIONS_LANG?.find((_m: any) => (String(detail?.chart_code) === String(_m?.code)))?.code || '', label: '' } : null;
                const _category = CATEGORY_TEMPLATE_VOTE_KEYWORD_OPTIONS_LANG?.find((_m: any) => (String(detail?.category) === String(_m?.value))) || CATEGORY_TEMPLATE_VOTE_KEYWORD_OPTIONS_LANG[0];

                formik.setFieldValue('name', _name);
                formik.setFieldValue('content', _content);

                formik.setFieldValue('chart_code', _chart_code);
                formik.setFieldValue('chart_title', _chart_title);
                formik.setFieldValue('chart_source', _chart_source);
                formik.setFieldValue('table_title', _table_title);

                setTimeout(() => {
                    formik.setFieldValue('category', _category);
                    formik.setErrors({});
                }, 300);
            } catch (error: any) {
                // setIsLoadingDetail((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, [JSON.stringify(detail)]);

    const _variables = useMemo(() => {
        return variables?.filter((item: any) => ((String(item?.value).toUpperCase()?.includes(String(search).toUpperCase()) || String(item?.description).toUpperCase()?.includes(String(search).toUpperCase()) || !search)));
    }, [search, JSON.stringify(variables)]);

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="xl"
                className={`border-header-footer modal-fullscreen`}
                scrollable={false}
                toggle={onCloseClick}
                keyboard={true}
            >
                <ModalHeader toggle={onCloseClick} className="modal-title-flex-grow-1">
                    <div className="d-flex flex-column gap-0 gap-sm-2 fs-18 fs-md-20">
                        <div className="flex-grow-1">
                            <>{!!detail?.id ? t('Button Update Body') : t('Button Create Body')}</>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="position-relative">
                    <div>
                        <form onSubmit={formik.handleSubmit}>
                            <Row>
                                <Col lg={isFullView ? 2 : 5} className="order-2 order-lg-1 mt-4 mt-lg-0">
                                    <div className="color-scrollbar-os table-responsive w-100" style={{ height: '100%', maxHeight: '770px', overflow: 'auto', borderRadius: 'var(--vz-border-radius)', border: ' var(--vz-border-width) solid var(--vz-input-border-custom)' }}>
                                        <table className="table table-borderless table-light table-hover table-nowrap mb-0">
                                            <thead className="table-primary" style={{ position: 'sticky', top: 0 }}>
                                                <tr>
                                                    <th style={{ width: '200px' }}>
                                                        <div className="d-flex align-items-center">
                                                            {t('Key')}
                                                            <div className="app-search p-0 ps-2">
                                                                <div className="position-relative" style={{
                                                                    width: '190px'
                                                                }}>
                                                                    <Input type="text" className="form-control bg-white" placeholder={`${t('Button Search')}...`}
                                                                        id="search-options"
                                                                        autoComplete="off"
                                                                        value={search}
                                                                        onChange={e => {
                                                                            setSearch((prev) => e.target.value);
                                                                        }}
                                                                    />
                                                                    <span style={{
                                                                        position: 'absolute',
                                                                        bottom: '2px',
                                                                        right: '5px',
                                                                        fontSize: '10px',
                                                                    }} className="text-danger">{_variables?.length || 0} {t('Results')} </span>
                                                                    <span className="mdi mdi-magnify search-widget-icon"></span>
                                                                    <span className="mdi mdi-close-circle search-widget-icon search-widget-icon-close d-none"
                                                                        id="search-close-options"></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th style={{ minWidth: '200px' }} className="align-middle">
                                                        {t('Example')}
                                                    </th>
                                                    <th style={{ minWidth: '200px' }} className="align-middle">
                                                        {t('Description')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(_variables || [])?.map((_key: any) => (
                                                    <tr key={_key?.id}>
                                                        <td>
                                                            <CopyWrapper contentCopy={_key?.value || ''} isShow={true}> {_key?.value || ''}</CopyWrapper>
                                                        </td>
                                                        <td>
                                                            {_key?.sample || '-'}
                                                        </td>
                                                        <td>
                                                            {_key?.description || '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{
                                        position: 'absolute',
                                        top: '0px',
                                        right: 1,
                                        width: '45px',
                                        height: '45px',
                                        fontSize: '30px',
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                        backgroundColor: 'var(--vz-primary)',
                                        color: 'var(--vz-white)',
                                        transform: 'translateX(-12px)',
                                        borderTopLeftRadius: '22px',
                                        borderBottomLeftRadius: '22px',
                                        borderTopRightRadius: '7px',
                                        cursor: 'pointer',
                                        boxShadow: '0px 0px 20px 0px rgba(76, 87, 125, 0.02)'
                                    }}
                                        onClick={() => setIsFullView((prev) => !prev)}
                                    >
                                        {isFullView ? <i className="mdi mdi-arrow-right-circle-outline" /> : <i className="mdi mdi-arrow-left-circle-outline" />}
                                    </div>
                                </Col>
                                <Col lg={isFullView ? 10 : 7} className="order-1 order-lg-2">
                                    <Row>
                                        <Col sm={12} lg={6} className="mb-3">
                                            <Label htmlFor="name" className="form-label">{t('Name')} <span className="text-danger"> *</span></Label>
                                            <Input
                                                name="name"
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                placeholder={t('Name')}
                                                value={formik?.values?.name}
                                                onChange={(event: any) => formik.setFieldValue('name', event?.target?.value)}
                                            />
                                            {formik.touched.name && formik.errors.name ? (
                                                <div className="text-danger mt-2">{formik.errors.name}</div>
                                            ) : null}
                                        </Col>
                                        <Col sm={12} lg={6} className="mb-3">
                                            <Label htmlFor="category" className="form-label">{t('Type')}<span className="text-danger"> *</span></Label>
                                            <DropdownOption
                                                name="category"
                                                dataList={CATEGORY_TEMPLATE_VOTE_KEYWORD_OPTIONS_LANG}
                                                placeholder={`${t('Type')}...`}
                                                isClearable={false}
                                                className="search-filter-category-type dropdown-status-rounded"
                                                classNamePrefix="name-prefix"
                                                initialValue={formik?.values?.category || CATEGORY_TEMPLATE_VOTE_KEYWORD_OPTIONS_LANG[0]}
                                                onChangeSelect={(event) => {
                                                    formik.setFieldValue('category', event);
                                                }} />
                                            {formik.touched.category && formik.errors.category ? (
                                                <div className="text-danger mt-2">{formik.errors.category}</div>
                                            ) : null}
                                        </Col>
                                        <Col sm={12} lg={6} className="mb-3">
                                            <Label htmlFor="chart_code" className="form-label">{t('Info-Graphic')}<span className="text-danger"> *</span></Label>
                                            <SearchTemplateChart name="chart_code"
                                                templates={TEMPLATE_CHART_OPTIONS_LANG}
                                                type='title'
                                                placeholder={`${t('Info-Graphic')}...`}
                                                isClearable={true}
                                                initialValue={formik?.values?.chart_code ?? undefined}
                                                onChangeSelect={(event) => {
                                                    formik.setFieldValue('chart_code', event);
                                                }} />
                                            {formik.touched.chart_code && formik.errors.chart_code ? (
                                                <div className="text-danger mt-2">{formik.errors.chart_code}</div>
                                            ) : null}
                                        </Col>
                                        <Col sm={12} lg={6} className="mb-3">
                                            <Label htmlFor="chart_title" className="form-label">{t('Info-Graphic Title')}</Label>
                                            <Input
                                                name="chart_title"
                                                type="text"
                                                className="form-control"
                                                id="chart_title"
                                                placeholder={t('Info-Graphic Title')}
                                                value={formik?.values?.chart_title}
                                                onChange={(event: any) => formik.setFieldValue('chart_title', event?.target?.value)}
                                            />
                                            {formik.touched.chart_title && formik.errors.chart_title ? (
                                                <div className="text-danger mt-2">{formik.errors.chart_title}</div>
                                            ) : null}
                                        </Col>
                                        <Col sm={12} lg={6} className="mb-3">
                                            <Label htmlFor="chart_source" className="form-label">{t('Info-Graphic Source')}</Label>
                                            <Input
                                                name="chart_source"
                                                type="text"
                                                className="form-control"
                                                id="chart_source"
                                                placeholder={t('Info-Graphic Source')}
                                                value={formik?.values?.chart_source}
                                                onChange={(event: any) => formik.setFieldValue('chart_source', event?.target?.value)}
                                            />
                                            {formik.touched.chart_source && formik.errors.chart_source ? (
                                                <div className="text-danger mt-2">{formik.errors.chart_source}</div>
                                            ) : null}
                                        </Col>
                                        <Col sm={12} lg={6} className="mb-3">
                                            <Label htmlFor="table_title" className="form-label">{t('Data-Table Title')}</Label>
                                            <Input
                                                name="table_title"
                                                type="text"
                                                className="form-control"
                                                id="table_title"
                                                placeholder={t('Data-Table Title')}
                                                value={formik?.values?.table_title}
                                                onChange={(event: any) => formik.setFieldValue('table_title', event?.target?.value)}
                                            />
                                            {formik.touched.table_title && formik.errors.table_title ? (
                                                <div className="text-danger mt-2">{formik.errors.table_title}</div>
                                            ) : null}
                                        </Col>
                                        <Col sm={12} className="mb-3">
                                            <Label htmlFor="content" className="form-label">{t('Template')} <span className="text-danger"> *</span></Label>
                                            <Input
                                                name="content"
                                                type="textarea"
                                                rows={20}
                                                className="form-control"
                                                id="content"
                                                placeholder={t('Template')}
                                                value={formik?.values?.content}
                                                onChange={(event: any) => formik.setFieldValue('content', event?.target?.value)}
                                            />
                                            {formik.touched.content && formik.errors.content ? (
                                                <div className="text-danger mt-2">{formik.errors.content}</div>
                                            ) : null}
                                        </Col>
                                        <Col sm={12} className="mb-2" >
                                            <div className="d-flex gap-3 justify-content-end align-item-center">
                                                <button
                                                    className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-item-center"
                                                    type="submit"
                                                    style={{ width: '150px' }}
                                                    disabled={isLoading}
                                                >
                                                    {isLoading && <span style={{ transform: 'translateY(1px)' }} className="me-1"><Spinner size="sm"></Spinner></span>}<span>{
                                                        !!detail?.id ? t('Button Update Body') : t('Button Create Body')
                                                    }</span>
                                                </button>
                                                <button
                                                    className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-item-center"
                                                    type="button"
                                                    onClick={onCloseClick}
                                                    style={{ width: '75px' }}
                                                >
                                                    <i className="ri-close-fill" style={{ lineHeight: '22px' }} /> <span className="ms-1">{t("Button Close")}</span>
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment >
    );
};

export default BodyForm;

